import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FileText, RefreshCw, Smartphone } from 'lucide-react';
import {
  buildCommunicationMessageFormData,
  fetchConversationWhatsAppTemplates,
  fetchDriverCustomerChannelStatus,
  fetchConversationMessages,
  hasCommunicationMedia,
  markConversationRead,
  recordConversationDeliveryContextEvent,
  sendConversationMessage,
  sendConversationWhatsAppTemplate,
  sendDriverCustomerMessage,
} from './api';
import { socket, socketAuthPayload } from '../../admin/chat/socket';
import ConversationDeliveryContext from './ConversationDeliveryContext';
import DeliverySlotInlineSelector from './DeliverySlotInlineSelector';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';

export default function ConversationThread({
  conversationId,
  currentUserId,
  currentUserType = 'admin',
  className = '',
  showInput = true,
  allowNotes = true,
  inputPlaceholder = 'Escribe un mensaje interno...',
  inputHelperText,
  onConversationLoaded,
  autoScrollMessages = true,
  autoScrollOnInitialLoad = true,
  enableDriverCustomerChannels = false,
  enableFinalCustomerWhatsApp = false,
  enableDeliverySlotSelection = false,
  showDeliveryContext = false,
  recentHistoryDays = 0,
  latestFirst = false,
  refreshSignal = 0,
  socketTicket = null,
}) {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [driverCustomerChannel, setDriverCustomerChannel] = useState(
    currentUserType === 'driver' ? 'fastmax_app' : 'internal'
  );
  const [driverCustomerStatus, setDriverCustomerStatus] = useState(null);
  const [customerWhatsAppTemplates, setCustomerWhatsAppTemplates] = useState([]);
  const [showCustomerWhatsAppTemplates, setShowCustomerWhatsAppTemplates] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateSendingId, setTemplateSendingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [olderCursor, setOlderCursor] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderError, setOlderError] = useState(null);
  const [realtimeStatus, setRealtimeStatus] = useState('connecting');
  const messagesContainerRef = useRef(null);
  const suppressNextAutoScrollRef = useRef(false);
  const pendingScrollRestoreRef = useRef(null);
  const olderLoadingRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const nearBottomRef = useRef(true);
  const activeConversationIdRef = useRef(conversationId);
  const requestSequenceRef = useRef(0);
  const liveMessageSequenceRef = useRef(0);
  activeConversationIdRef.current = conversationId;
  const useLatestWindow = latestFirst || recentHistoryDays > 0;
  const driverCustomerEnabled = (
    enableDriverCustomerChannels
    && currentUserType === 'driver'
    && conversation?.type === 'delivery'
    && conversation?.delivery_point_id
  );
  const finalCustomerWhatsAppEnabled = (
    enableFinalCustomerWhatsApp
    && ['admin', 'store', 'provider'].includes(currentUserType)
    && conversation?.type === 'delivery'
    && conversation?.delivery_point_id
  );
  const deliverySlotSelectionEnabled = (
    enableDeliverySlotSelection
    && ['store', 'provider'].includes(currentUserType)
    && conversation?.type === 'delivery'
    && conversation?.delivery_point_id
  );
  const deliveryCustomerStatusEnabled = driverCustomerEnabled || finalCustomerWhatsAppEnabled;
  const adminCustomerWhatsAppEnabled = finalCustomerWhatsAppEnabled && currentUserType === 'admin';
  const driverWhatsAppAvailable = Boolean(driverCustomerStatus?.whatsapp_available);
  const rawWhatsAppDisabledReason = driverCustomerStatus?.whatsapp_disabled_reason || (
    currentUserType === 'driver'
      ? 'WhatsApp no disponible. Usa Cliente App.'
      : 'WhatsApp no disponible. Usa Interno Fast Max.'
  );
  const driverWhatsAppDisabledReason = currentUserType === 'driver'
    ? rawWhatsAppDisabledReason
    : rawWhatsAppDisabledReason.replace('Usa Cliente App.', 'Usa Interno Fast Max.');

  const canUseDeliveryCustomerChannels = useCallback((nextConversation) => (
    nextConversation?.type === 'delivery'
    && nextConversation?.delivery_point_id
    && (
      (enableDriverCustomerChannels && currentUserType === 'driver')
      || (enableFinalCustomerWhatsApp && ['admin', 'store', 'provider'].includes(currentUserType))
    )
  ), [currentUserType, enableDriverCustomerChannels, enableFinalCustomerWhatsApp]);

  const loadMessages = useCallback(async () => {
    if (!conversationId) return;
    const requestSequence = ++requestSequenceRef.current;
    const liveSequence = liveMessageSequenceRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchConversationMessages(conversationId, {
        currentUserType,
        currentUserId,
      }, useLatestWindow ? {
        window: 'latest',
        limit: 50,
        ...(recentHistoryDays ? { recent_days: recentHistoryDays } : {}),
      } : {});
      if (activeConversationIdRef.current !== conversationId || requestSequence !== requestSequenceRef.current) return;
      setConversation(data.conversation);
      onConversationLoaded?.(data.conversation);
      if (canUseDeliveryCustomerChannels(data.conversation)) {
        try {
          const status = await fetchDriverCustomerChannelStatus(conversationId);
          setDriverCustomerStatus(status || null);
          if (!status?.whatsapp_available) {
            setDriverCustomerChannel(currentUserType === 'driver' ? 'fastmax_app' : 'internal');
          }
        } catch (statusError) {
          console.error('Customer WhatsApp status error:', statusError);
          setDriverCustomerStatus({
            whatsapp_available: false,
            whatsapp_disabled_reason: 'No se pudo verificar WhatsApp. Actualiza para reintentar.',
          });
          setDriverCustomerChannel(currentUserType === 'driver' ? 'fastmax_app' : 'internal');
        }
      } else {
        setDriverCustomerStatus(null);
        setDriverCustomerChannel(currentUserType === 'driver' ? 'fastmax_app' : 'internal');
      }
      if (activeConversationIdRef.current !== conversationId || requestSequence !== requestSequenceRef.current) return;
      if (!autoScrollOnInitialLoad) {
        suppressNextAutoScrollRef.current = true;
      }
      setMessages(prev => {
        const fetched = data.messages || [];
        if (liveSequence === liveMessageSequenceRef.current) return fetched;
        const seen = new Set(fetched.map(item => `${item.channel}-${item.id}`));
        return [...fetched, ...prev.filter(item =>
          Number(item.conversation_id) === Number(conversationId)
          && !seen.has(`${item.channel}-${item.id}`)
        )];
      });
      setHasOlderMessages(useLatestWindow ? Boolean(data.has_more) : false);
      setOlderCursor(data.next_before_id
        ? { before_id: data.next_before_id }
        : data.next_before_at ? { before_at: data.next_before_at } : null);
      setOlderError(null);
      await markConversationRead(conversationId, {
        recipient_type: currentUserType,
        user_id: currentUserId,
        driver_id: currentUserType === 'driver' ? currentUserId : undefined,
      });
    } catch (err) {
      if (requestSequence !== requestSequenceRef.current) return;
      console.error('Communication thread error:', err);
      setError('No se pudo cargar la conversación');
    } finally {
      if (requestSequence === requestSequenceRef.current) setIsLoading(false);
    }
  }, [
    autoScrollOnInitialLoad,
    canUseDeliveryCustomerChannels,
    conversationId,
    currentUserId,
    currentUserType,
    onConversationLoaded,
    recentHistoryDays,
    useLatestWindow,
  ]);

  const loadOlderMessages = useCallback(async () => {
    if (!useLatestWindow || !conversationId || !hasOlderMessages || !olderCursor || olderLoadingRef.current) return;
    olderLoadingRef.current = true;
    setLoadingOlder(true);
    setOlderError(null);
    try {
      const data = await fetchConversationMessages(conversationId, {
        currentUserType,
        currentUserId,
      }, { window: 'latest', limit: 50, ...olderCursor });
      if (activeConversationIdRef.current !== conversationId) return;
      const olderMessages = data.messages || [];
      if (olderMessages.length) {
        const container = messagesContainerRef.current;
        pendingScrollRestoreRef.current = container
          ? { height: container.scrollHeight, top: container.scrollTop }
          : null;
        suppressNextAutoScrollRef.current = true;
        setMessages(prev => {
          const existing = new Set(prev.map(item => `${item.channel}-${item.id}`));
          return [...olderMessages.filter(item => !existing.has(`${item.channel}-${item.id}`)), ...prev];
        });
      }
      setHasOlderMessages(Boolean(data.has_more));
      setOlderCursor(data.next_before_id
        ? { before_id: data.next_before_id }
        : data.next_before_at ? { before_at: data.next_before_at } : null);
    } catch (err) {
      console.error('Older communication messages error:', err);
      setOlderError('No se pudieron cargar mensajes anteriores. Reintenta.');
    } finally {
      olderLoadingRef.current = false;
      setLoadingOlder(false);
    }
  }, [conversationId, currentUserId, currentUserType, hasOlderMessages, olderCursor, useLatestWindow]);

  useLayoutEffect(() => {
    const snapshot = pendingScrollRestoreRef.current;
    const container = messagesContainerRef.current;
    if (!snapshot || !container) return;
    container.scrollTop = snapshot.top + container.scrollHeight - snapshot.height;
    pendingScrollRestoreRef.current = null;
    lastScrollTopRef.current = 0;
    nearBottomRef.current = true;
  }, [messages]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages, refreshSignal]);

  useEffect(() => {
    setDriverCustomerChannel(currentUserType === 'driver' ? 'fastmax_app' : 'internal');
    setDriverCustomerStatus(null);
    setCustomerWhatsAppTemplates([]);
    setShowCustomerWhatsAppTemplates(false);
    setConversation(null);
    setMessages([]);
    setHasOlderMessages(false);
    setOlderCursor(null);
    setOlderError(null);
    pendingScrollRestoreRef.current = null;
    setError(null);
  }, [conversationId, currentUserType]);

  useEffect(() => {
    if (!conversationId) return undefined;

    const join = () => {
      if (currentUserType === 'final_customer' && !socketTicket) return;
      setRealtimeStatus('connecting');
      socket.emit('join_conversation', socketAuthPayload({
        conversation_id: conversationId,
        ...(currentUserType === 'final_customer' ? { viewer_type: 'final_customer', socket_ticket: socketTicket } : {}),
      }), acknowledgement => {
        setRealtimeStatus(acknowledgement?.ok ? 'live' : 'unavailable');
        if (!acknowledgement?.ok) console.warn('Conversation room join denied:', acknowledgement?.error);
      });
    };
    const handleMessage = (message) => {
      const payloadMessage = message?.message || message;
      if (Number(payloadMessage?.conversation_id) !== Number(conversationId)) return;
      if (!payloadMessage?.id) return;
      liveMessageSequenceRef.current += 1;
      setMessages(prev => {
        if (prev.some(item => item.id === payloadMessage.id && item.channel === payloadMessage.channel)) return prev;
        return [...prev, payloadMessage];
      });
    };
    const handleConversationUpdated = (payload) => {
      if (Number(payload?.conversation_id) !== Number(conversationId)) return;
      if (payload.conversation) {
        setConversation(payload.conversation);
        onConversationLoaded?.(payload.conversation);
      }
    };
    const handleReadUpdated = (payload) => {
      if (Number(payload?.conversation_id) !== Number(conversationId)) return;
      if (payload.conversation) {
        setConversation(payload.conversation);
        onConversationLoaded?.(payload.conversation);
      }
    };
    const handleConnect = () => {
      join();
      loadMessages();
    };
    const reconcile = () => {
      if (document.visibilityState === 'hidden') return;
      if (socket.connected) join();
      loadMessages();
    };
    const handleDisconnect = () => setRealtimeStatus('unavailable');

    join();
    socket.on('conversation_message_created', handleMessage);
    socket.on('conversation_updated', handleConversationUpdated);
    socket.on('conversation_read_updated', handleReadUpdated);
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleDisconnect);
    window.addEventListener('fastmax:auth-token-updated', join);
    window.addEventListener('focus', reconcile);
    window.addEventListener('online', reconcile);
    document.addEventListener('visibilitychange', reconcile);

    return () => {
      window.removeEventListener('fastmax:auth-token-updated', join);
      window.removeEventListener('focus', reconcile);
      window.removeEventListener('online', reconcile);
      document.removeEventListener('visibilitychange', reconcile);
      socket.off('conversation_message_created', handleMessage);
      socket.off('conversation_updated', handleConversationUpdated);
      socket.off('conversation_read_updated', handleReadUpdated);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleDisconnect);
      socket.emit('leave_conversation', { conversation_id: conversationId });
    };
  }, [conversationId, currentUserType, loadMessages, onConversationLoaded, socketTicket]);

  useEffect(() => {
    if (!autoScrollMessages) return;
    if (useLatestWindow && !nearBottomRef.current) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    if (suppressNextAutoScrollRef.current) {
      suppressNextAutoScrollRef.current = false;
      return;
    }

    const scrollToBottom = () => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
      nearBottomRef.current = true;
    };

    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      window.requestAnimationFrame(scrollToBottom);
    } else {
      scrollToBottom();
    }
  }, [autoScrollMessages, messages, useLatestWindow]);

  const handleDeliverySlotUpdated = useCallback((deliveryPoint) => {
    if (!deliveryPoint?.id) return;
    setConversation(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        delivery: {
          ...(prev.delivery || {}),
          ...deliveryPoint,
        },
      };
    });
  }, []);

  const handleDeliveryContextEvent = useCallback((eventType) => {
    if (!conversationId) return;
    recordConversationDeliveryContextEvent(
      conversationId,
      eventType,
      currentUserType === 'store' ? 'store_chat' : 'conversation_thread',
    ).catch(eventError => {
      // Observability is best effort and must never interrupt the chat.
      console.error('No se pudo registrar la interaccion con la entrega:', eventError);
    });
  }, [conversationId, currentUserType]);

  async function handleToggleCustomerWhatsAppTemplates() {
    if (showCustomerWhatsAppTemplates) {
      setShowCustomerWhatsAppTemplates(false);
      return;
    }
    setTemplatesLoading(true);
    setError(null);
    try {
      const data = await fetchConversationWhatsAppTemplates(conversationId);
      setCustomerWhatsAppTemplates(data.templates || []);
      setShowCustomerWhatsAppTemplates(true);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'No se pudieron cargar las plantillas WhatsApp.');
    } finally {
      setTemplatesLoading(false);
    }
  }

  async function handleSendCustomerWhatsAppTemplate(templateId) {
    setTemplateSendingId(templateId);
    setError(null);
    try {
      const result = await sendConversationWhatsAppTemplate(conversationId, templateId);
      if (result.message) {
        setMessages(prev => prev.some(item => item.id === result.message.id && item.channel === result.message.channel)
          ? prev : [...prev, result.message]);
      }
      try {
        const status = await fetchDriverCustomerChannelStatus(conversationId);
        setDriverCustomerStatus(status || null);
        if (!status?.whatsapp_available) setDriverCustomerChannel('internal');
      } catch (statusError) {
        console.error('Customer WhatsApp status refresh error:', statusError);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message || 'No se pudo enviar la plantilla WhatsApp.');
    } finally {
      setTemplateSendingId(null);
    }
  }

  async function handleSend(payload) {
    let message;
    try {
      if (driverCustomerEnabled) {
        const selectedChannel = driverCustomerChannel === 'whatsapp' && driverWhatsAppAvailable
          ? 'whatsapp'
          : 'fastmax_app';
        const requestPayload = hasCommunicationMedia(payload)
          ? buildCommunicationMessageFormData({
              ...payload,
              channel: selectedChannel,
            })
          : {
              content: payload.content,
              channel: selectedChannel,
            };
        const result = await sendDriverCustomerMessage(conversationId, requestPayload);
        message = result.message;
        if (selectedChannel === 'whatsapp') {
          const status = await fetchDriverCustomerChannelStatus(conversationId);
          setDriverCustomerStatus(status || null);
        }
      } else if (finalCustomerWhatsAppEnabled && driverCustomerChannel === 'whatsapp') {
        if (!driverWhatsAppAvailable) {
          throw new Error(driverWhatsAppDisabledReason);
        }
        const requestPayload = hasCommunicationMedia(payload)
          ? buildCommunicationMessageFormData({
              ...payload,
              channel: 'whatsapp',
            })
          : {
              content: payload.content,
              channel: 'whatsapp',
            };
        const result = await sendDriverCustomerMessage(conversationId, requestPayload);
        message = result.message;
        try {
          const status = await fetchDriverCustomerChannelStatus(conversationId);
          setDriverCustomerStatus(status || null);
          if (!status?.whatsapp_available) setDriverCustomerChannel('internal');
        } catch (statusError) {
          console.error('Customer WhatsApp status refresh error:', statusError);
        }
      } else {
        const requestPayload = {
          ...payload,
          sender_type: currentUserType,
          sender_user_id: currentUserId,
          sender_driver_id: currentUserType === 'driver' ? currentUserId : undefined,
        };
        message = await sendConversationMessage(
          conversationId,
          hasCommunicationMedia(payload) ? buildCommunicationMessageFormData(requestPayload) : requestPayload
        );
      }
    } catch (err) {
      const status = err?.response?.status;
      const messageText = err?.response?.data?.message || err?.response?.data?.error || err.message || 'No se pudo enviar el mensaje.';
      if (status === 428 && deliveryCustomerStatusEnabled) {
        setDriverCustomerChannel(currentUserType === 'driver' ? 'fastmax_app' : 'internal');
        setDriverCustomerStatus(prev => ({
          ...(prev || {}),
          whatsapp_available: false,
          whatsapp_disabled_reason: messageText,
        }));
      }
      setError(messageText);
      throw err;
    }
    if (!message) return;
    liveMessageSequenceRef.current += 1;
    setMessages(prev => {
      if (prev.some(item => item.id === message.id && item.channel === message.channel)) return prev;
      return [...prev, message];
    });
  }

  if (!conversationId) {
    return (
      <div className={`flex items-center justify-center bg-slate-50 text-slate-500 ${className}`}>
        Selecciona una conversación
      </div>
    );
  }

  return (
    <div className={`flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50 sm:min-h-[420px] ${className}`}>
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-3 sm:px-4">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-extrabold text-slate-900 font-montserrat">
            {showDeliveryContext && conversation?.delivery
              ? (conversation.delivery.recipient_name || 'Cliente final')
              : 'Comunicacion Fast Max'}
          </h3>
          <p className="truncate text-xs text-slate-500 font-montserrat">
            {conversation?.delivery_point_id
              ? `Entrega #${conversation.delivery_point_id}${conversation?.delivery?.status ? ` | ${conversation.delivery.status}` : ''}`
              : `Conversacion #${conversationId}`}
          </p>
        </div>
        <button
          onClick={loadMessages}
          disabled={isLoading}
          className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-50 sm:py-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {realtimeStatus === 'unavailable' && (
        <p role="status" className="border-b border-amber-100 bg-amber-50 px-3 py-1 text-xs text-amber-800">
          Tiempo real no disponible. Usa Actualizar para reconciliar mensajes.
        </p>
      )}

      {showDeliveryContext && conversation?.delivery_point_id && (
        <ConversationDeliveryContext
          delivery={{
            ...(conversation.delivery || {}),
            id: conversation.delivery?.id || conversation.delivery_point_id,
          }}
          onEvent={handleDeliveryContextEvent}
        />
      )}

      {deliverySlotSelectionEnabled && (
        <DeliverySlotInlineSelector
          conversationId={conversationId}
          conversation={conversation}
          onSlotUpdated={handleDeliverySlotUpdated}
        />
      )}

      <div
        data-testid="conversation-messages"
        ref={messagesContainerRef}
        onScroll={(event) => {
          const currentTop = event.currentTarget.scrollTop;
          if (currentTop <= 80 && currentTop < lastScrollTopRef.current) loadOlderMessages();
          lastScrollTopRef.current = currentTop;
          nearBottomRef.current = event.currentTarget.scrollHeight
            - event.currentTarget.clientHeight - currentTop <= 80;
        }}
        className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 sm:p-4"
      >
        {useLatestWindow && (hasOlderMessages || loadingOlder || olderError) && (
          <div className="flex flex-col items-center gap-1 text-xs">
            <button
              type="button"
              onClick={loadOlderMessages}
              disabled={loadingOlder || !hasOlderMessages}
              className="rounded-full bg-white px-3 py-1.5 font-semibold text-[#B1336E] shadow-sm disabled:opacity-60"
            >
              {loadingOlder ? 'Cargando mensajes anteriores...' : 'Cargar mensajes anteriores'}
            </button>
            {olderError && <span className="text-red-700">{olderError}</span>}
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!error && messages.length === 0 && !isLoading && (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
            Sin mensajes todavía. Usa este hilo para centralizar notas, WhatsApp y coordinación interna.
          </div>
        )}

        {messages.map(message => (
          <MessageBubble key={`${message.channel}-${message.id}`} message={message} currentUserType={currentUserType} />
        ))}
      </div>

      {showInput && (
        <>
          {driverCustomerEnabled && (
            <div className="border-t border-slate-200 bg-white px-2 py-2 sm:px-3">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDriverCustomerChannel('fastmax_app')}
                  className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold sm:flex-none ${
                    driverCustomerChannel === 'fastmax_app' ? 'bg-[#B1336E] text-white' : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  <FileText className="h-4 w-4" />
                  Cliente App
                </button>
                <button
                  type="button"
                  onClick={() => driverWhatsAppAvailable && setDriverCustomerChannel('whatsapp')}
                  disabled={!driverWhatsAppAvailable}
                  className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none ${
                    driverCustomerChannel === 'whatsapp' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700'
                  }`}
                  title={driverWhatsAppAvailable ? 'Enviar por WhatsApp' : driverWhatsAppDisabledReason}
                >
                  <Smartphone className="h-4 w-4" />
                  WhatsApp
                </button>
                {!driverWhatsAppAvailable && (
                  <span className="text-[11px] font-semibold text-amber-700">{driverWhatsAppDisabledReason}</span>
                )}
              </div>
            </div>
          )}
          {finalCustomerWhatsAppEnabled && (
            <div className="border-t border-slate-200 bg-white px-2 py-2 sm:px-3">
              {adminCustomerWhatsAppEnabled && (
                <p className="mb-2 text-[11px] font-semibold text-slate-600">
                  WhatsApp cliente final: {driverCustomerStatus?.phone_number || 'número no disponible'}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDriverCustomerChannel('internal')}
                  className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold sm:flex-none ${
                    driverCustomerChannel === 'internal' ? 'bg-[#B1336E] text-white' : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  <FileText className="h-4 w-4" />
                  Interno Fast Max
                </button>
                <button
                  type="button"
                  onClick={() => driverWhatsAppAvailable && setDriverCustomerChannel('whatsapp')}
                  disabled={!driverWhatsAppAvailable}
                  className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none ${
                    driverCustomerChannel === 'whatsapp' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700'
                  }`}
                  title={driverWhatsAppAvailable ? 'Enviar al WhatsApp del cliente final' : driverWhatsAppDisabledReason}
                >
                  <Smartphone className="h-4 w-4" />
                  WhatsApp cliente
                </button>
                {!driverWhatsAppAvailable && (
                  <span className="text-[11px] font-semibold text-amber-700">{driverWhatsAppDisabledReason}</span>
                )}
                {adminCustomerWhatsAppEnabled && driverCustomerStatus?.phone_number && (
                  <button
                    type="button"
                    onClick={handleToggleCustomerWhatsAppTemplates}
                    disabled={templatesLoading || Boolean(templateSendingId)}
                    className="inline-flex min-h-[40px] items-center justify-center rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 disabled:opacity-50"
                  >
                    {templatesLoading ? 'Cargando...' : 'Plantillas WhatsApp'}
                  </button>
                )}
              </div>
              {adminCustomerWhatsAppEnabled && showCustomerWhatsAppTemplates && (
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  {customerWhatsAppTemplates.map(template => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => handleSendCustomerWhatsAppTemplate(template.id)}
                      disabled={!template.available || Boolean(templateSendingId)}
                      title={template.disabled_reason || template.description}
                      className="rounded-lg border border-slate-200 bg-white p-2 text-left text-xs hover:border-[#B1336E] disabled:opacity-50"
                    >
                      <span className="block font-bold text-slate-900">{template.label}</span>
                      <span className="text-slate-500">{templateSendingId === template.id ? 'Enviando...' : template.disabled_reason || template.description}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <MessageInput
            onSend={handleSend}
            disabled={isLoading || Boolean(templateSendingId)}
            allowNotes={!driverCustomerEnabled && driverCustomerChannel !== 'whatsapp' && allowNotes}
            placeholder={driverCustomerEnabled
              ? (driverCustomerChannel === 'whatsapp' ? 'Escribe al WhatsApp del cliente...' : 'Escribe al cliente final...')
              : finalCustomerWhatsAppEnabled && driverCustomerChannel === 'whatsapp'
                ? 'Escribe al WhatsApp del cliente final...'
                : inputPlaceholder}
            helperText={driverCustomerEnabled
              ? (driverCustomerChannel === 'whatsapp'
                  ? 'Mensaje libre al WhatsApp del cliente final. Solo disponible con ventana de 24h abierta.'
                  : 'Mensaje visible para el cliente final dentro de Fast Max App y registrado en esta entrega.')
              : finalCustomerWhatsAppEnabled && driverCustomerChannel === 'whatsapp'
                ? 'Mensaje libre al WhatsApp del cliente final. Solo disponible con ventana de 24h abierta y queda registrado en esta entrega.'
                : inputHelperText}
          />
        </>
      )}
    </div>
  );
}
